<script lang="ts">
    import type { HTMLButtonAttributes } from 'svelte/elements';
    import { ComponentType } from 'svelte';
    import type { VariantProps } from 'class-variance-authority';
    import { cva } from 'class-variance-authority';

    import cn from '$common/utils/cn';

    const button = cva('vp-button', {
        variants: {
            intent: {
                primary: 'vp-button--primary',
                secondary: 'vp-button--secondary',
                tertiary: 'vp-button--tertiary',
                negative: 'vp-button--negative',
                inverse: 'vp-button--inverse',
                link: 'bg-none bg-transparent text-content-neutral underline',
            },
            shape: {
                squared: 'vp-button--squared',
                rounded: 'vp-button--rounded',
            },
            size: {
                none: 'p-0',
                small: 'vp-button--small',
                medium: 'vp-button--medium',
                large: 'vp-button--large',
            },
            state: {
                disabled: 'opacity-[0.38] cursor-not-allowed',
            },
        },
        compoundVariants: [],
    });

    /**
     * For Svelte components, we recommend setting your defaultVariants within
     * Svelte props (which are `undefined` by default)
     */
    interface $$Props
        extends HTMLButtonAttributes,
            VariantProps<typeof button> {
        startIcon?: ComponentType;
        endIcon?: ComponentType;
    }

    /**
     * For Svelte components, we recommend setting your defaultVariants within
     * Svelte props (which are `undefined` by default)
     */
    export let intent: $$Props['intent'] = 'primary';
    export let size: $$Props['size'] = 'medium';
    export let shape: $$Props['shape'] = 'squared';
    export let disabled: $$Props['disabled'] = false;
    export let state: $$Props['state'] = undefined;
    export let startIcon: $$Props['startIcon'] = undefined;
    export let endIcon: $$Props['endIcon'] = undefined;
</script>

<button
    {...$$props}
    on:click
    {disabled}
    class={cn(button({ intent, size, shape, state }), $$props.class)}
>
    <span class="vp-button__label">
        {#if startIcon}
            <span data-part="start-slot">
                <svelte:component this={startIcon} />
            </span>
        {/if}
        <slot />
        {#if endIcon}
            <span data-part="end-slot">
                <svelte:component this={endIcon} />
            </span>
        {/if}
    </span>
</button>
